import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { OnboardingShell } from "../components/OnboardingShell";
import { DURATION, EASE_OUT_SOFT } from "../lib/motion";
import type { OnboardingAnswers, LanguageCode } from "./OnboardingFlow";

/**
 * Beyond Sure — Tailored Intro Screens (v3.6).
 *
 * 3-4 full-screen "here's what I'll do for you" cards. The set is CHOSEN
 * at mount time based on the user's onboarding answers — role-based
 * tailoring, the way Stripe / Linear / Notion onboardings adapt to the
 * user's selected job-to-be-done.
 *
 * Mapping logic (see chooseIntroSet below):
 *   • goal === "health-cover"      → lead with comparison + cashless networks
 *   • goal === "family-income"     → lead with term-life comparison
 *   • goal === "retirement"        → lead with long-horizon wealth audit
 *   • goal === "audit-existing"    → lead with OCR + truth-audit
 *   • policy uploaded              → ALWAYS include the OCR + truth-audit
 *                                    cards so the user immediately sees what
 *                                    they just uploaded gets used.
 *   • existing.length > 0          → include renewal reminders
 *   • language ≠ "en"              → first card name-drops the chosen
 *                                    language so the user knows their pick
 *                                    is real.
 *
 * Each intro screen is rendered inside OnboardingShell so it carries the
 * brand background + glass card + motion automatically. We add an `eyebrow`
 * ("WHAT I CAN DO · 2 of 4") so the user sees this is a finite preview, not
 * yet another quiz step.
 *
 * The set always ends with a final card whose Continue label is "Open
 * BeyondSure" — that's the hand-off into the chatbot placeholder.
 */

type IntroCardKey =
  | "language-welcome"
  | "compare-insurers"
  | "ocr-truth-audit"
  | "renewal-reminders"
  | "term-life"
  | "best-plan"
  | "personalized-wealth"
  // v6.2 — partner-path cards
  | "partner-dashboard"
  | "partner-commission"
  | "partner-leads";

interface IntroCardDef {
  key: IntroCardKey;
  eyebrow: string;
  title: string;
  helper: string;
  visualKind:
    | "chat" | "ocr" | "compare" | "audit" | "renewal" | "best" | "term" | "wealth"
    // v6.2 partner visuals — kept abstract (no real product yet) so they
    // gesture at the value without making promises the dashboard can't keep.
    | "partner-dashboard" | "partner-commission" | "partner-leads";
}

const ALL_CARDS: Record<IntroCardKey, IntroCardDef> = {
  "language-welcome": {
    key: "language-welcome",
    eyebrow: "WHAT I CAN DO",
    title: "I'll meet you in your language.",
    helper:
      "Every conversation, every recommendation, every policy summary — in the language you picked. Switch any time.",
    visualKind: "chat",
  },
  "compare-insurers": {
    key: "compare-insurers",
    eyebrow: "WHAT I CAN DO",
    title: "I'll compare 6 insurers for you in ~2 minutes.",
    helper:
      "HDFC ERGO, ICICI Lombard, Bajaj Allianz, TATA AIG, Star Health, Niva Bupa — side by side, in plain language.",
    visualKind: "compare",
  },
  "ocr-truth-audit": {
    key: "ocr-truth-audit",
    eyebrow: "WHAT I CAN DO",
    title: "I'll read your existing policy and fact-check it.",
    helper:
      "Upload a PDF — I extract every field, flag missing fine print, and check the claim settlement claim against the regulator's data.",
    visualKind: "audit",
  },
  "renewal-reminders": {
    key: "renewal-reminders",
    eyebrow: "WHAT I CAN DO",
    title: "You'll never miss a renewal again.",
    helper:
      "I track expiry across all your policies and nudge you 30, 7 and 1 day before — so you stay covered without thinking about it.",
    visualKind: "renewal",
  },
  "term-life": {
    key: "term-life",
    eyebrow: "WHAT I CAN DO",
    title: "If something happens to you, they're okay.",
    helper:
      "Term life is the cheapest, highest-impact cover you'll ever buy. I compare 8 insurers and pick the right cover for your income.",
    visualKind: "term",
  },
  "best-plan": {
    key: "best-plan",
    eyebrow: "WHAT I CAN DO",
    title: "I'll narrow it down to the best plan for you.",
    helper:
      "After comparing the field, I tell you the ONE I'd pick if I were you — and exactly why. No middle-of-the-fence answers.",
    visualKind: "best",
  },
  "personalized-wealth": {
    key: "personalized-wealth",
    eyebrow: "WHAT I CAN DO",
    title: "I plan the long horizon, not just this year.",
    helper:
      "Retirement, kids' education, the lump-sum for a house. I work backwards from your milestones — not from a product catalogue.",
    visualKind: "wealth",
  },
  // v6.2 — partner-path cards
  "partner-dashboard": {
    key: "partner-dashboard",
    eyebrow: "PARTNER · BUILT FOR YOU",
    title: "Your client book, organised — finally.",
    helper:
      "Every policy you've sold, in one place. Renewal calendar, claim status, NB pipeline — searchable, exportable, mobile-friendly.",
    visualKind: "partner-dashboard",
  },
  "partner-commission": {
    key: "partner-commission",
    eyebrow: "PARTNER · BUILT FOR YOU",
    title: "Commission, reconciled in real time.",
    helper:
      "Reads insurer payouts as they happen, matches to your sold policies, flags the misses. No more month-end reconciliation pain.",
    visualKind: "partner-commission",
  },
  "partner-leads": {
    key: "partner-leads",
    eyebrow: "PARTNER · BUILT FOR YOU",
    title: "Warm leads, scored and ready.",
    helper:
      "Tell us your product mix; we route in-network customer leads that already match your specialty. No cold-calling spreadsheets.",
    visualKind: "partner-leads",
  },
};

/**
 * Picks 3-4 intro cards based on the user's answers. Always ends with the
 * "best plan" card so the set lands on a confidence-building closer.
 */
function chooseIntroSet(a: OnboardingAnswers): IntroCardDef[] {
  // v6.2 — Partner path bypasses the consumer cards entirely. Showing a
  // term-life comparison card to an IRDAI agent would be a brand mismatch
  // (they sell term life — they don't need us to compare it). The partner
  // set is 3-4 cards: language welcome (if non-en) → dashboard → commission
  // → leads. Ends on "leads" because that's the strongest hook for partners.
  if (a.persona && a.persona !== "customer") {
    const partnerPicked: IntroCardKey[] = [];
    if (a.languages.some((c) => c !== "en")) partnerPicked.push("language-welcome");
    partnerPicked.push("partner-dashboard", "partner-commission", "partner-leads");
    return partnerPicked.slice(0, 4).map((k) => ALL_CARDS[k]);
  }

  const picked: IntroCardKey[] = [];

  // Language card — only if the user picked at least one non-English language.
  // v6.2: languages is now a multi-select array (0-2 codes). We show the
  // welcome card if there's any non-"en" code in the pick.
  if (a.languages.some((c) => c !== "en")) picked.push("language-welcome");

  // Lead card by goal
  switch (a.goal) {
    case "health-cover":
      picked.push("compare-insurers");
      break;
    case "family-income":
      picked.push("term-life");
      break;
    case "retirement":
      picked.push("personalized-wealth");
      break;
    case "audit-existing":
      picked.push("ocr-truth-audit");
      break;
    default:
      picked.push("compare-insurers");
  }

  // Policy uploaded OR has existing policies → always show the OCR card
  if (a.policyUpload.fileName || a.existing.some((e) => e !== "none")) {
    if (!picked.includes("ocr-truth-audit")) picked.push("ocr-truth-audit");
    if (!picked.includes("renewal-reminders")) picked.push("renewal-reminders");
  }

  // Always close with the "best plan" card so the user lands on confidence
  if (!picked.includes("best-plan")) picked.push("best-plan");

  // Cap at 4 — never more
  return picked.slice(0, 4).map((k) => ALL_CARDS[k]);
}

interface IntroFlowProps {
  answers: OnboardingAnswers;
  onComplete: () => void;
  onSkipAll?: () => void;
  initialStep?: number;
}

export function IntroFlow({ answers, onComplete, onSkipAll, initialStep = 1 }: IntroFlowProps) {
  const cards = useMemo(() => chooseIntroSet(answers), [answers]);
  const [step, setStep] = useState(Math.max(1, Math.min(cards.length, initialStep)));
  const [direction, setDirection] = useState<1 | -1>(1);

  function goNext() {
    if (step >= cards.length) {
      onComplete();
      return;
    }
    setDirection(1);
    setStep((s) => s + 1);
  }
  function goBack() {
    if (step <= 1) return;
    setDirection(-1);
    setStep((s) => s - 1);
  }

  const card = cards[step - 1];
  const isLast = step === cards.length;

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={card.key}
        initial={{ opacity: 0, x: direction > 0 ? 24 : -24 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: direction > 0 ? -24 : 24 }}
        transition={{ duration: DURATION.base, ease: EASE_OUT_SOFT }}
      >
        <OnboardingShell
          stepIndex={step}
          totalSteps={cards.length}
          eyebrow={`${card.eyebrow} · ${step} OF ${cards.length}`}
          title={card.title}
          helper={card.helper}
          onBack={step > 1 ? goBack : null}
          onContinue={goNext}
          continueLabel={isLast ? "Open BeyondSure" : "Continue"}
          onSkip={!isLast ? onSkipAll : undefined}
        >
          <IntroVisual
            kind={card.visualKind}
            userName={answers.name || "you"}
            primaryLang={answers.languages[0] ?? null}
          />
        </OnboardingShell>
      </motion.div>
    </AnimatePresence>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Intro visuals — small brand visuals using the same vocabulary as
// PhoneShowcase so the brand voice is consistent.
// ════════════════════════════════════════════════════════════════════════

function IntroVisual({
  kind,
  userName,
  primaryLang,
}: {
  kind: IntroCardDef["visualKind"];
  userName: string;
  primaryLang: LanguageCode | null;
}) {
  switch (kind) {
    case "chat":
      return <ChatVisual userName={userName} primaryLang={primaryLang} />;
    case "compare":
      return <CompareVisual />;
    case "audit":
      return <AuditVisual />;
    case "renewal":
      return <RenewalVisual />;
    case "term":
      return <TermVisual />;
    case "best":
      return <BestVisual />;
    case "wealth":
      return <WealthVisual />;
    case "partner-dashboard":
      return <PartnerDashboardVisual />;
    case "partner-commission":
      return <PartnerCommissionVisual />;
    case "partner-leads":
      return <PartnerLeadsVisual />;
    default:
      return null;
  }
}

/* v6.2 — greetings cover all 22 scheduled Indian languages + English.
 * The Mix group is gone — picking 2 codes implies a mix, and we just use
 * the FIRST picked language for the greeting (e.g. ["hi","en"] → नमस्ते).
 * Urdu / Kashmiri / Sindhi use Naskh Arabic via the multilingual fallback. */
const LANG_GREETINGS: Record<LanguageCode, string> = {
  en: "Hi",
  hi: "नमस्ते",
  bn: "হাই",
  mr: "नमस्कार",
  te: "హాయ్",
  ta: "வணக்கம்",
  gu: "નમસ્તે",
  ur: "ہیلو",
  kn: "ಹಲೋ",
  or: "ନମସ୍କାର",
  ml: "ഹായ്",
  pa: "ਸਤ ਸ੍ਰੀ ਅਕਾਲ",
  as: "নমস্কাৰ",
  mai: "प्रणाम",
  // v6.2 additions
  sa:  "नमस्ते",        // Sanskrit (Devanagari)
  ks:  "آداب",         // Kashmiri (Naskh Arabic)
  sd:  "سلام",         // Sindhi (Naskh Arabic)
  kok: "नमस्कार",       // Konkani (Devanagari)
  ne:  "नमस्ते",        // Nepali (Devanagari)
  mni: "খুরুমজরি",      // Manipuri (Bengali-script fallback; fallbackRisk)
  brx: "जदों",          // Bodo (Devanagari)
  doi: "नमस्कार",       // Dogri (Devanagari)
  sat: "ᱡᱚᱦᱟᱨ",         // Santhali (Ol Chiki; fallbackRisk)
};

function ChatVisual({ userName, primaryLang }: { userName: string; primaryLang: LanguageCode | null }) {
  const greeting = primaryLang ? LANG_GREETINGS[primaryLang] : "Hi";
  return (
    <div className="flex flex-col gap-2.5 p-4 rounded-[var(--radius-lg)] bg-[var(--color-mint)]">
      <div
        className="self-start max-w-[80%] rounded-[14px] rounded-tl-[4px] px-3.5 py-2.5 text-[13px]"
        style={{ backgroundColor: "var(--color-brand-soft)", color: "var(--color-brand-deep)" }}
      >
        <span className="bsure-multilingual" style={{ fontWeight: 700 }}>{greeting}</span>, {userName} — kya cover karna hai?
      </div>
      <div
        className="self-end max-w-[80%] rounded-[14px] rounded-tr-[4px] px-3.5 py-2.5 text-[13px]"
        style={{ backgroundColor: "var(--color-ink)", color: "var(--color-paper)" }}
      >
        Health for my parents — both 60+
      </div>
      <div
        className="self-start max-w-[80%] rounded-[14px] rounded-tl-[4px] px-3.5 py-2.5 text-[13px]"
        style={{ backgroundColor: "var(--color-brand-soft)", color: "var(--color-brand-deep)" }}
      >
        Got it. I'll find senior-citizen plans with low waiting periods…
      </div>
    </div>
  );
}

function CompareVisual() {
  const rows = [
    { name: "HDFC ERGO",     pct: 86 },
    { name: "ICICI Lombard", pct: 72 },
    { name: "Bajaj Allianz", pct: 64 },
    { name: "TATA AIG",      pct: 58 },
    { name: "Star Health",   pct: 48 },
    { name: "Niva Bupa",     pct: 40 },
  ];
  return (
    <div className="flex flex-col gap-1.5 p-4 rounded-[var(--radius-lg)] bg-[var(--color-mint)]">
      {rows.map((r, i) => (
        <motion.div
          key={r.name}
          initial={{ opacity: 0, x: -8 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.05 + i * 0.06 }}
          className="flex items-center gap-2.5"
        >
          <span className="w-[82px] shrink-0 text-[12px] font-medium text-[var(--color-ink-80)]">
            {r.name}
          </span>
          <span className="relative flex-1 h-[6px] rounded-full overflow-hidden bg-[var(--color-ink-08)]">
            <motion.span
              initial={{ width: 0 }}
              animate={{ width: `${r.pct}%` }}
              transition={{ delay: 0.1 + i * 0.06, duration: 0.5, ease: EASE_OUT_SOFT }}
              className="absolute inset-y-0 left-0 rounded-full"
              style={{
                background:
                  "linear-gradient(90deg, var(--color-brand-deep), var(--color-brand) 60%, var(--color-brand-vivid))",
              }}
            />
          </span>
        </motion.div>
      ))}
    </div>
  );
}

function AuditVisual() {
  const items: { label: string; ok: boolean; note: string }[] = [
    { label: "Cashless network",  ok: true,  note: "5,000+ hospitals" },
    { label: "Waiting period",    ok: false, note: "2 years for PED" },
    { label: "Claim settlement",  ok: true,  note: "98.2% (FY24, verified)" },
    { label: "Room rent capping", ok: false, note: "1% of sum insured" },
  ];
  return (
    <div className="flex flex-col gap-2 p-4 rounded-[var(--radius-lg)] bg-[var(--color-mint)]">
      {items.map((row, i) => (
        <motion.div
          key={row.label}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 + i * 0.07 }}
          className="flex items-center gap-2.5"
        >
          <span
            className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
            style={{
              backgroundColor: row.ok ? "var(--color-brand-soft)" : "rgb(255 211 0 / 0.18)",
            }}
          >
            {row.ok ? (
              <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
                <path d="M3 8.5l3 3 7-7" stroke="var(--color-brand-deep)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ) : (
              <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
                <path d="M8 4v5M8 11.5v0.5" stroke="#8a6b00" strokeWidth="2" strokeLinecap="round" />
              </svg>
            )}
          </span>
          <span className="flex-1 text-[12.5px] text-[var(--color-ink)]">{row.label}</span>
          <span className="text-[11.5px] text-[var(--color-ink-60)]">{row.note}</span>
        </motion.div>
      ))}
    </div>
  );
}

function RenewalVisual() {
  return (
    <div className="flex flex-col gap-2 p-4 rounded-[var(--radius-lg)] bg-[var(--color-mint)]">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="rounded-[12px] p-3.5 flex items-start gap-3"
        style={{ backgroundColor: "var(--color-brand-soft)" }}
      >
        <span
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bsure-pulse"
          style={{ backgroundColor: "var(--color-brand-deep)" }}
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="6" stroke="var(--color-paper)" strokeWidth="1.8" />
            <path d="M8 4v4l3 2" stroke="var(--color-paper)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
        <div className="flex flex-col">
          <span className="bsure-caps text-[9.5px] text-[var(--color-brand-deep)]">Renewal due</span>
          <span className="text-[13.5px] font-semibold text-[var(--color-ink)] mt-0.5">TATA AIG renews in 12 days</span>
          <span className="text-[11.5px] text-[var(--color-ink-60)] mt-0.5">₹14,200 · auto-pay scheduled</span>
        </div>
      </motion.div>
      <div className="rounded-[10px] px-3 py-2.5 flex items-center justify-between border border-[var(--color-ink-08)] bg-[var(--color-paper)]">
        <span className="text-[12px] text-[var(--color-ink)]">Niva Bupa · renews in 48 days</span>
        <span className="text-[10.5px] text-[var(--color-brand-deep)] font-semibold">on track</span>
      </div>
    </div>
  );
}

function TermVisual() {
  const rows = [
    { name: "HDFC Click 2 Protect", price: "₹14,200 / yr" },
    { name: "Max Life Smart Wealth", price: "₹16,800 / yr" },
    { name: "ICICI Pru iProtect",   price: "₹15,500 / yr" },
  ];
  return (
    <div className="flex flex-col gap-2.5 p-4 rounded-[var(--radius-lg)] bg-[var(--color-mint)]">
      <div className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-brand-deep)] font-semibold">
        ₹1 crore cover · 35 years old · non-smoker
      </div>
      {rows.map((r, i) => (
        <motion.div
          key={r.name}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 + i * 0.06 }}
          className="flex items-center justify-between rounded-[10px] px-3 py-2.5 border border-[var(--color-ink-08)] bg-[var(--color-paper)]"
        >
          <span className="text-[12.5px] text-[var(--color-ink)] font-medium">{r.name}</span>
          <span className="text-[12.5px] text-[var(--color-ink-80)] tabular-nums">{r.price}</span>
        </motion.div>
      ))}
    </div>
  );
}

function BestVisual() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-[14px] p-4"
      style={{
        background:
          "linear-gradient(135deg, var(--color-brand-deep) 0%, var(--color-canvas) 100%)",
        color: "var(--color-paper)",
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <span className="bsure-caps text-[9.5px] opacity-70">My pick for you</span>
          <div className="bsure-display text-[18px] leading-[1.1] mt-1" style={{ fontWeight: 700 }}>
            HDFC Optima Restore
          </div>
          <div className="text-[12px] mt-1 opacity-85">
            ₹11,400 / yr · ₹10L cover · 5★ rated
          </div>
        </div>
        <span
          className="inline-flex h-7 px-2.5 items-center rounded-full text-[9.5px] font-semibold uppercase tracking-[0.12em]"
          style={{
            backgroundColor: "var(--color-brand-vivid)",
            color: "var(--color-canvas)",
          }}
        >
          #1
        </span>
      </div>
      <div className="mt-3 pt-3 border-t border-[var(--color-paper-24)] text-[11.5px] opacity-85">
        Why this · cashless, no co-pay, low waiting period
      </div>
    </motion.div>
  );
}

function WealthVisual() {
  const milestones = [
    { year: "Now",     label: "Cover + emergency",    fill: 100 },
    { year: "+5 yrs",  label: "Kids' education ready", fill: 45 },
    { year: "+15 yrs", label: "House lump-sum",       fill: 18 },
    { year: "+25 yrs", label: "Retirement corpus",    fill: 5 },
  ];
  return (
    <div className="flex flex-col gap-2 p-4 rounded-[var(--radius-lg)] bg-[var(--color-mint)]">
      {milestones.map((m, i) => (
        <motion.div
          key={m.year}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 + i * 0.06 }}
          className="flex items-center gap-3"
        >
          <span className="w-[64px] shrink-0 text-[11.5px] font-semibold text-[var(--color-brand-deep)] tabular-nums">
            {m.year}
          </span>
          <span className="flex-1 text-[12.5px] text-[var(--color-ink)]">{m.label}</span>
          <span className="w-[48px] h-[6px] rounded-full bg-[var(--color-ink-08)] overflow-hidden">
            <motion.span
              initial={{ width: 0 }}
              animate={{ width: `${m.fill}%` }}
              transition={{ duration: 0.55, delay: 0.1 + i * 0.06 }}
              className="block h-full rounded-full"
              style={{ backgroundColor: "var(--color-brand-deep)" }}
            />
          </span>
        </motion.div>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// v6.2 — Partner-path visuals (kept abstract, no real product yet)
// ════════════════════════════════════════════════════════════════════════

function PartnerDashboardVisual() {
  const rows = [
    { name: "TATA AIG · 2024", status: "active",     stat: "₹14k" },
    { name: "HDFC ERGO · 2024", status: "renewal-12d", stat: "₹26k" },
    { name: "ICICI · 2024",     status: "active",     stat: "₹9k"  },
    { name: "Bajaj · 2024",     status: "claim-open", stat: "—"    },
  ];
  return (
    <div className="flex flex-col gap-1.5 p-4 rounded-[var(--radius-lg)] bg-[var(--color-mint)]">
      {rows.map((r, i) => (
        <motion.div
          key={r.name}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 + i * 0.06 }}
          className="flex items-center justify-between gap-2 rounded-[10px] px-3 py-2.5 border border-[var(--color-ink-08)] bg-[var(--color-paper)]"
        >
          <span className="text-[12.5px] font-medium text-[var(--color-ink)]">{r.name}</span>
          <span className="flex items-center gap-2">
            <span
              className="text-[10px] font-semibold uppercase tracking-[0.1em]"
              style={{
                color:
                  r.status === "active"     ? "var(--color-brand-deep)" :
                  r.status === "renewal-12d" ? "#8a6b00" :
                  r.status === "claim-open"  ? "#9a3a3a" :
                  "var(--color-ink-60)",
              }}
            >
              {r.status === "active" ? "ACTIVE" : r.status === "renewal-12d" ? "RENEWAL 12D" : "CLAIM OPEN"}
            </span>
            <span className="text-[11.5px] text-[var(--color-ink-60)] tabular-nums w-[40px] text-right">{r.stat}</span>
          </span>
        </motion.div>
      ))}
    </div>
  );
}

function PartnerCommissionVisual() {
  const rows = [
    { month: "Mar", paid: "₹1.42L", expected: "₹1.50L", match: true },
    { month: "Feb", paid: "₹1.10L", expected: "₹1.10L", match: true },
    { month: "Jan", paid: "₹0.94L", expected: "₹1.08L", match: false },
  ];
  return (
    <div className="flex flex-col gap-1.5 p-4 rounded-[var(--radius-lg)] bg-[var(--color-mint)]">
      <div className="grid grid-cols-4 gap-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--color-ink-60)] mb-1">
        <span>Month</span><span>Paid</span><span>Expected</span><span className="text-right">Match</span>
      </div>
      {rows.map((r, i) => (
        <motion.div
          key={r.month}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 + i * 0.06 }}
          className="grid grid-cols-4 gap-2 items-center rounded-[10px] px-3 py-2 border border-[var(--color-ink-08)] bg-[var(--color-paper)]"
        >
          <span className="text-[12px] text-[var(--color-ink)]">{r.month}</span>
          <span className="text-[12px] text-[var(--color-ink-80)] tabular-nums">{r.paid}</span>
          <span className="text-[12px] text-[var(--color-ink-80)] tabular-nums">{r.expected}</span>
          <span className="text-[11px] font-semibold text-right" style={{ color: r.match ? "var(--color-brand-deep)" : "#9a3a3a" }}>
            {r.match ? "OK" : "GAP"}
          </span>
        </motion.div>
      ))}
    </div>
  );
}

function PartnerLeadsVisual() {
  const leads = [
    { name: "Anita G. · 32 · BLR",    fit: "Health · ₹10L",    score: 92 },
    { name: "Rohan M. · 38 · MUM",    fit: "Term · ₹1Cr",       score: 85 },
    { name: "Devika S. · 45 · DEL",   fit: "Health Family · ₹15L", score: 78 },
  ];
  return (
    <div className="flex flex-col gap-1.5 p-4 rounded-[var(--radius-lg)] bg-[var(--color-mint)]">
      {leads.map((l, i) => (
        <motion.div
          key={l.name}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 + i * 0.06 }}
          className="flex items-center justify-between gap-2 rounded-[10px] px-3 py-2.5 border border-[var(--color-ink-08)] bg-[var(--color-paper)]"
        >
          <div className="flex flex-col">
            <span className="text-[12.5px] font-medium text-[var(--color-ink)]">{l.name}</span>
            <span className="text-[11px] text-[var(--color-ink-60)]">{l.fit}</span>
          </div>
          <span
            className="inline-flex h-7 w-12 items-center justify-center rounded-full text-[11px] font-semibold tabular-nums"
            style={{
              backgroundColor: "var(--color-brand-soft)",
              color: "var(--color-brand-deep)",
            }}
          >
            {l.score}
          </span>
        </motion.div>
      ))}
    </div>
  );
}

export default IntroFlow;
